import api from './api';

export interface Service {
  id: string;
  name: string;
  description: string;
  price_type: 'fixed' | 'from' | 'per_unit';
  price: number;
  unit?: string;
  is_active: boolean;
  price_display: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceStats {
  total_services: number;
  active_services: number;
  inactive_services: number;
  price_types: {
    fixed: number;
    from: number;
    per_unit: number;
  };
}

export interface CreateServiceData {
  name: string;
  description: string;
  price_type: 'fixed' | 'from' | 'per_unit';
  price: number;
  unit?: string;
  is_active?: boolean;
}

export interface UpdateServiceData extends Partial<CreateServiceData> {}

class ServiceApiService {
  /**
   * Récupère tous les services
   */
  async getServices(isActive?: boolean): Promise<Service[]> {
    try {
      const params = isActive !== undefined ? { is_active: isActive } : {};
      const response = await api.get('/api/services/', { params });
      
      // L'API Django peut retourner une structure paginée
      if (response.data.results) {
        return response.data.results;
      }
      
      // Ou directement un tableau
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération des services:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des services');
    }
  }

  /**
   * Récupère un service par son ID
   */
  async getService(id: string): Promise<Service> {
    try {
      const response = await api.get(`/api/services/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération du service:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération du service');
    }
  }

  /**
   * Crée un nouveau service
   */
  async createService(serviceData: CreateServiceData): Promise<Service> {
    try {
      const response = await api.post('/api/services/', serviceData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création du service:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        // Erreurs de validation
        const errors = Object.values(error.response.data).flat();
        throw new Error(errors.join(', '));
      }
      throw new Error('Erreur lors de la création du service');
    }
  }

  /**
   * Met à jour un service
   */
  async updateService(id: string, serviceData: UpdateServiceData): Promise<Service> {
    try {
      const response = await api.patch(`/api/services/${id}/`, serviceData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du service:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        // Erreurs de validation
        const errors = Object.values(error.response.data).flat();
        throw new Error(errors.join(', '));
      }
      throw new Error('Erreur lors de la mise à jour du service');
    }
  }

  /**
   * Supprime un service
   */
  async deleteService(id: string): Promise<void> {
    try {
      await api.delete(`/api/services/${id}/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du service:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la suppression du service');
    }
  }

  /**
   * Active/désactive un service
   */
  async toggleServiceStatus(id: string): Promise<Service> {
    try {
      const response = await api.post(`/api/services/${id}/toggle-status/`);
      return response.data.service;
    } catch (error: any) {
      console.error('Erreur lors du changement de statut:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du changement de statut');
    }
  }

  /**
   * Récupère les statistiques des services
   */
  async getServiceStats(): Promise<ServiceStats> {
    try {
      const response = await api.get('/api/services/stats/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des statistiques');
    }
  }

  /**
   * Formate le prix selon le type
   */
  formatPrice(service: Service): string {
    switch (service.price_type) {
      case 'fixed':
        return `${service.price} FCFA`;
      case 'from':
        return `À partir de ${service.price} FCFA`;
      case 'per_unit':
        return `${service.price} FCFA par ${service.unit}`;
      default:
        return `${service.price} FCFA`;
    }
  }

  /**
   * Valide les données d'un service
   */
  validateServiceData(data: CreateServiceData | UpdateServiceData): string[] {
    const errors: string[] = [];

    if ('name' in data && (!data.name || data.name.trim().length === 0)) {
      errors.push('Le nom du service est requis');
    }

    if ('description' in data && (!data.description || data.description.trim().length === 0)) {
      errors.push('La description du service est requise');
    }

    if ('price' in data && (data.price === undefined || data.price < 0)) {
      errors.push('Le prix doit être un nombre positif');
    }

    if ('price_type' in data && data.price_type === 'per_unit' && (!data.unit || data.unit.trim().length === 0)) {
      errors.push('L\'unité est requise pour un prix par unité');
    }

    return errors;
  }
}

export const serviceApiService = new ServiceApiService();
